// Private helper functions
const helpers = {
  // traverse first array and see if it's IDs are in the subsequent Sets.
  intersect: (searchData) => {
    if (searchData.length === 1) {
      return [...searchData[0]];
    } if (searchData.length === 2) {
      return [...searchData[0]]
        .filter((data) => searchData[1].has(data));
    } if (searchData.length === 3) {
      return [...searchData[0]]
        .filter((data) => searchData[1].has(data) && searchData[2].has(data));
    }
    return [];
  },
  // get the union for each category.
  union: (searchCriteria, categoriesToIds) => {
    let union = [];
    for (let i = 0; i < searchCriteria.length; i += 1) {
      // make sure waffles map has a Set of IDs.
      if (categoriesToIds.has(searchCriteria[i])) {
        const setOfIds = categoriesToIds.get(searchCriteria[i].toLowerCase());
        union = [...union,
          ...[...setOfIds.values()]];
      }
    }
    // Takes care of duplicates
    return new Set(union);
  },
  // return map of other categories currently not searched.
  difference: (keys, categories) => {
    const categoryKeys = Object.keys(categories);
    const map = new Map();
    categoryKeys.forEach((eachKey) => {
      if (!keys.includes(eachKey)) {
        map.set(eachKey, [...categories[eachKey].keys()]);
      }
    });
    return map;
  },
  // today/commerce/occasion/christmas
  parseCommercePath: (path) => {
    const pathSplit = path.split('/');
    return {
      categoryName: pathSplit[2],
      category: pathSplit[3],
    };
  },
};

class WaffleIron {
  constructor(items) {
    if (!items) {
      throw new Error('Missing items in VanillaWaffle package.');
    }

    // this is a map of id => article
    // this is used for when we are trying to pull up all the
    // articles after we filter by category and Ids.
    this.items = new Map();

    // this is a map of category => Set([id])
    // we use this for filtering (unions and intersect)
    // the outcome of those operations from this map
    // will be used to retrieve articles from this.items
    this.categoriesToIds = new Map();

    // this will store all the categories searchable based on data
    // returned from curation category => Map(key(category from path) => name)
    this.categories = {
      occasion: new Map(),
      recipient: new Map(),
      interest: new Map(),
    };

    this.initializeItems(items);
  }

  initializeItems = (items) => {
    items.forEach((item) => {
      const { item: article, id } = item;

      const commerce = (article && article.taxonomy && article.taxonomy.commerce) || [];

      this.items.set(id, item);

      commerce.forEach((commerceTerm) => {
        const { name, path } = commerceTerm;
        const { categoryName, category } = helpers.parseCommercePath(path);

        if (category && !this.categories[categoryName].get(category)) {
          this.categories[categoryName].set(category, name);
        }
        if (this.categoriesToIds.has(category)) {
          const tempSet = this.categoriesToIds.get(category);
          tempSet.add(id);
        } else {
          this.categoriesToIds.set(category, new Set().add(id));
        }
      });
    });
  };

  // get waffle size
  getSize() {
    return this.items.size;
  }

  getAllItems = () => [...this.items.values()];

  getContent = (data) => data.map((eachData) => this.items.get(eachData));

  // argument: an Map with { key: {occasion|who|interests}, value: ['christmas', 'birthday] }
  // for each search criteria we want to take the union first
  // after taking the union of each search criteria.
  // then we take the intersect which is an [Set, Set, Set]
  getFilteredItems = (searchMap) => {
    const keys = [...searchMap.keys()];
    // nothing was searched, return [];
    if (keys.length === 0) {
      return [];
    } if (keys.length === 1) {
      // only one category is searched, so just get the union and convert to array
      return this.getContent([...helpers.union(searchMap.get(keys[0]), this.categoriesToIds)]);
    }
    const searchData = [];
    keys.forEach((key) => {
      const criteria = searchMap.get(key);
      const getUnionBetweenCategory = helpers.union(criteria, this.categoriesToIds);
      searchData.push(getUnionBetweenCategory);
    });
    const waffleIntersection = helpers.intersect(searchData);
    return this.getContent(waffleIntersection);
  };

  // TODO rewrite how we determine active, this class owns the logic
  getAllCategories = (disabledSet) => {
    const { occasion, recipient, interest } = this.categories;

    // this will map through the categories and return the key, actual display name
    // and whether or not it is disabled from argument.
    const getKeyAndName = (map) => [...map.keys()]
      .map((key) => ({
        key,
        name: map.get(key),
        active: !disabledSet ? true : !disabledSet.has(key),
      }));

    return {
      occasion: getKeyAndName(occasion),
      recipient: getKeyAndName(recipient),
      interest: getKeyAndName(interest),
    };
  };

  // TODO determine if MVP
  // Gets updated filters with disabled set given searchMap
  // argument: an Map with { key: {occasion|who|interests}, value: ['christmas', 'birthday] }
  getUpdatedFilters = (searchMap, existingValSet) => {
    if (searchMap.size === 0) {
      return this.getAllCategories(null);
    }
    const disabledSet = new Set();
    const keys = [...Object.keys(searchMap)];
    const diffMap = helpers.difference(keys, this.categories);
    const diffKeys = [...diffMap.keys()];
    // for each key, get the filters and loop through
    // each one and see if any data gets returned.
    diffKeys.forEach((eachKey) => {
      const filterValues = diffMap.get(eachKey);
      const tempSearchMap = new Map(searchMap);
      filterValues.forEach((value) => {
        // set up search criteria
        tempSearchMap.set(eachKey, [value]);
        // pass search into getWaffles and see if any data returns
        const contentFromNewSearch = this.getFilteredItems(tempSearchMap);
        // if no search result add to disabledSet.
        if (contentFromNewSearch.length === 0 && !existingValSet.has(value)) {
          disabledSet.add(value);
        }
      });
    });

    return this.getAllCategories(disabledSet);
  };
}

export default WaffleIron;
